import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, Flex, Switch, Text, TextField } from '@chatsdet/ui';
import { Microphone, Stop, Trash } from '@phosphor-icons/react';
import { ConfigSection } from './ConfigSection';
import { AttachmentPickerDialog } from '../../../features/attachments/components/AttachmentPickerDialog';
import { useResolvedAttachments, useUploadAttachments, attachmentRawUrl } from '../../../features/attachments/api';

type AudioNodeConfigProps = {
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
};

/**
 * Pick the best MediaRecorder mime type. Note: MediaRecorder may *report* a
 * `video/webm` container for audio-only recordings on some browsers, so we
 * always relabel the uploaded File to an `audio/*` type that matches the
 * chosen container — the bytes are valid either way, and our upload validator
 * whitelists `audio/*`.
 */
const pickMimeType = (): { recorderMime: string; uploadMime: string; extension: string } => {
  const candidates: Array<{ recorderMime: string; uploadMime: string; extension: string }> = [
    { recorderMime: 'audio/webm;codecs=opus', uploadMime: 'audio/webm', extension: 'webm' },
    { recorderMime: 'audio/webm', uploadMime: 'audio/webm', extension: 'webm' },
    { recorderMime: 'audio/ogg;codecs=opus', uploadMime: 'audio/ogg', extension: 'ogg' },
    { recorderMime: 'audio/mp4', uploadMime: 'audio/mp4', extension: 'mp4' },
  ];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c.recorderMime)) {
      return c;
    }
  }
  return { recorderMime: '', uploadMime: 'audio/webm', extension: 'webm' };
};

const formatElapsed = (ms: number) => {
  const totalSec = Math.floor(ms / 1000);
  const mm = Math.floor(totalSec / 60)
    .toString()
    .padStart(2, '0');
  const ss = (totalSec % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
};

export const AudioNodeConfig: React.FC<AudioNodeConfigProps> = ({ localData, setLocalData }) => {
  const label = (localData.label as string) ?? '';
  const loop = (localData.loop as boolean) ?? false;
  const waitForCompletion = (localData.waitForCompletion as boolean) ?? true;
  const attachmentIds = Array.isArray(localData.attachmentIds)
    ? (localData.attachmentIds as unknown[]).filter(
        (id): id is string => typeof id === 'string' && id.length > 0
      )
    : [];
  const attachmentId = attachmentIds[0];
  const resolved = useResolvedAttachments(attachmentId ? [attachmentId] : []);
  const attachment = resolved.data?.data?.[0];

  const upload = useUploadAttachments();

  // ── Recorder state ────────────────────────────────────────────────────────
  const [recording, setRecording] = React.useState(false);
  const [recorderError, setRecorderError] = React.useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = React.useState(0);
  const [level, setLevel] = React.useState(0); // 0..1 normalised mic amplitude
  const recorderRef = React.useRef<MediaRecorder | null>(null);
  const chunksRef = React.useRef<Blob[]>([]);
  const streamRef = React.useRef<MediaStream | null>(null);
  const audioCtxRef = React.useRef<AudioContext | null>(null);
  const analyserRef = React.useRef<AnalyserNode | null>(null);
  const rafRef = React.useRef<number | null>(null);
  const startedAtRef = React.useRef<number>(0);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const teardownAudio = React.useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    try {
      analyserRef.current?.disconnect();
    } catch {
      // analyser may already be disconnected
    }
    analyserRef.current = null;
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    if (ctx && ctx.state !== 'closed') {
      void ctx.close().catch(() => {});
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startRecording = async () => {
    setRecorderError(null);
    setElapsedMs(0);
    setLevel(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const { recorderMime, uploadMime, extension } = pickMimeType();
      const rec = recorderMime
        ? new MediaRecorder(stream, { mimeType: recorderMime })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const recordedSeconds = (Date.now() - startedAtRef.current) / 1000;
        teardownAudio();
        setRecording(false);
        // Force audio/* mime on upload — MediaRecorder sometimes reports a
        // video/webm container for audio-only streams, and our validator
        // rejects video/*.
        const blob = new Blob(chunksRef.current, { type: uploadMime });
        const filename = `recording-${new Date().toISOString().replace(/[:.]/g, '-')}.${extension}`;
        const file = new File([blob], filename, { type: uploadMime });
        try {
          const res = await upload.mutateAsync([file]);
          const uploaded = res.data[0];
          if (uploaded) {
            setLocalData((prev) => ({
              ...prev,
              attachmentIds: [uploaded.id],
              label: prev.label || `Recording (${recordedSeconds.toFixed(1)}s)`,
            }));
          }
        } catch (err) {
          setRecorderError(err instanceof Error ? err.message : 'Upload failed');
        }
      };
      rec.start();
      recorderRef.current = rec;
      startedAtRef.current = Date.now();
      setRecording(true);

      // Live amplitude meter
      const Ctor = window.AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new Ctor();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.fftSize);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        // Non-linear boost so quiet speech still moves the meter
        setLevel(Math.min(1, Math.pow(rms * 2.2, 0.7)));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);

      // Timer tick (state-driven for the display; RAF would over-render)
      timerRef.current = setInterval(() => {
        setElapsedMs(Date.now() - startedAtRef.current);
      }, 100);
    } catch (err) {
      teardownAudio();
      setRecording(false);
      setRecorderError(err instanceof Error ? err.message : 'Microphone access denied');
    }
  };

  const stopRecording = () => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        // recorder may already be stopped
      }
    } else {
      teardownAudio();
      setRecording(false);
    }
  };

  React.useEffect(() => {
    return () => {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        try {
          recorderRef.current.stop();
        } catch {
          // recorder may already be stopped
        }
      }
      teardownAudio();
    };
  }, [teardownAudio]);

  // ── Preview ───────────────────────────────────────────────────────────────
  const previewUrl = attachment ? attachmentRawUrl(attachment.id) : null;

  return (
    <div className="space-y-4">
      <Text size="sm" className="text-muted-foreground">
        Pipe a WAV (or webm/ogg) clip into the browser's microphone. The next action
        that calls <code>getUserMedia()</code> — or any action that already holds the
        microphone track — will hear this audio.
      </Text>

      <TextField
        label="Label"
        placeholder="e.g. “Say hello”"
        value={label}
        onChange={(v) => setLocalData((prev) => ({ ...prev, label: v }))}
      />

      <ConfigSection title="Audio clip" count={attachmentId ? 1 : 0}>
        {attachmentId && attachment ? (
          <div className="rounded-md bg-muted/30 px-3 py-2">
            <Flex align="center" gap="sm">
              <div className="flex-1 min-w-0">
                <Text size="sm" className="truncate">{attachment.filename}</Text>
                <Text size="xs" className="text-muted-foreground">
                  {(attachment.sizeBytes / 1024).toFixed(1)} KB · {attachment.contentType}
                </Text>
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<Trash size={14} />}
                onPress={() => setLocalData((prev) => ({ ...prev, attachmentIds: [] }))}
              >
                Remove
              </Button>
            </Flex>
            {previewUrl && (
              <audio controls src={previewUrl} className="mt-2 w-full" preload="metadata" />
            )}
          </div>
        ) : (
          <Text size="sm" className="text-muted-foreground">
            No clip selected. Pick an existing audio attachment or record one below.
          </Text>
        )}

        <AnimatePresence mode="wait" initial={false}>
          {recording ? (
            <motion.div
              key="recording"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18 }}
              className="mt-3 rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-3"
            >
              <Flex align="center" gap="sm">
                {/* Pulsing mic orb — ring scales with live input level */}
                <div className="relative flex h-10 w-10 items-center justify-center">
                  <motion.span
                    className="absolute inset-0 rounded-full bg-red-500/25"
                    animate={{ scale: 1 + level * 0.9, opacity: 0.35 + level * 0.5 }}
                    transition={{ type: 'spring', stiffness: 220, damping: 18 }}
                  />
                  <motion.span
                    className="absolute inset-1 rounded-full bg-red-500/40"
                    animate={{ scale: [1, 1.15, 1] }}
                    transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <span className="relative h-2.5 w-2.5 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]" />
                </div>

                <div className="flex-1 min-w-0">
                  <Text size="sm" className="font-medium text-red-600">
                    Recording…
                  </Text>
                  <Text size="xs" className="tabular-nums text-muted-foreground">
                    {formatElapsed(elapsedMs)}
                  </Text>
                </div>

                {/* Live waveform bars */}
                <div className="flex h-8 items-end gap-[2px]" aria-hidden>
                  {[0.4, 0.7, 1, 0.85, 0.55, 0.8, 0.5].map((weight, i) => (
                    <motion.span
                      key={i}
                      className="w-1 rounded-sm bg-red-500"
                      animate={{
                        height: `${Math.max(12, Math.min(100, level * weight * 130 + 12))}%`,
                      }}
                      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  onClick={stopRecording}
                  aria-label="Stop recording"
                  className="group relative ml-2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-500 text-white shadow-[0_0_0_0_rgba(239,68,68,0.6)] outline-none transition-all hover:bg-red-600 focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2"
                >
                  <motion.span
                    className="absolute inset-0 rounded-full bg-red-500"
                    animate={{ scale: [1, 1.35], opacity: [0.45, 0] }}
                    transition={{ duration: 1.1, repeat: Infinity, ease: 'easeOut' }}
                  />
                  <Stop size={14} weight="fill" className="relative" />
                </button>
              </Flex>
            </motion.div>
          ) : upload.isPending ? (
            <motion.div
              key="uploading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-3 rounded-lg border border-border bg-muted/30 px-3 py-3"
            >
              <Flex align="center" gap="sm">
                <motion.span
                  className="h-2.5 w-2.5 rounded-full bg-indigo-500"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
                <Text size="sm" className="text-muted-foreground">
                  Uploading recording…
                </Text>
              </Flex>
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Flex gap="sm" className="mt-3">
                <AttachmentPickerDialog
                  selectedIds={attachmentIds}
                  onChange={(nextIds) =>
                    setLocalData((prev) => ({
                      ...prev,
                      attachmentIds: nextIds.slice(0, 1),
                    }))
                  }
                  triggerVariant="outline"
                  triggerSize="sm"
                  triggerLabel={attachmentId ? 'Replace clip' : 'Choose clip'}
                  initialKind="audio"
                />
                <Button
                  variant="outline"
                  size="sm"
                  iconLeft={<Microphone size={14} />}
                  onPress={startRecording}
                >
                  Record
                </Button>
              </Flex>
            </motion.div>
          )}
        </AnimatePresence>

        {recorderError && (
          <Text size="xs" className="mt-2 text-destructive">
            {recorderError}
          </Text>
        )}
      </ConfigSection>

      <ConfigSection title="Playback">
        <Flex direction="column" gap="sm">
          <Switch
            isSelected={loop}
            onChange={(v) => setLocalData((prev) => ({ ...prev, loop: v }))}
            label="Loop until the next audio node"
          />
          <Switch
            isSelected={waitForCompletion}
            onChange={(v) => setLocalData((prev) => ({ ...prev, waitForCompletion: v }))}
            isDisabled={loop}
            label="Wait for clip to finish before advancing"
          />
        </Flex>
      </ConfigSection>
    </div>
  );
};
